import { useEffect, useMemo, useState } from 'react';
import { Icon, IconButton, LinearProgress, TablePagination, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Button, Typography, AlertColor } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { WorkflowsService } from 'shared/services/workflows/workflowsService';
import { IListagemWorkflow, listKeyValueStatus, listKeyValueTipo } from 'shared/types';
import { FerramentasDaListagem, IListaFiltros, AlertaSnackBar } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { currentDateHour, getValueFromKeyValue, stringToDate, stringToDateTime } from 'shared/utils';

import { useSelectedOrganization } from 'shared/store/organization';
import { useHasPermissionAcao } from 'shared/hooks';

export const ListagemDeWorkflows: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const [rows, setRows] = useState<IListagemWorkflow[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [eventoPesquisar, setEventoPesquisar] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const hasPermitionCreate = useHasPermissionAcao('workflow','CRE');
  const hasPermitionRead = useHasPermissionAcao('workflow','REA');

  const org = useSelectedOrganization();

  const buscaId = useMemo(() => {
    return searchParams.get('idWorkflow') || [];
  }, [searchParams]);

  const buscaNome = useMemo(() => {
    return searchParams.get('nmWorkflow') || '';
  }, [searchParams]);

  const buscaDescricao = useMemo(() => {
    return searchParams.get('dsWorkflow') || '';
  }, [searchParams]);

  const buscaStatus = useMemo(() => {
    return searchParams.get('stWorkflow') || '';
  }, [searchParams]);

  const buscaTipo = useMemo(() => {
    return searchParams.get('tpWorkflow') || '';
  }, [searchParams]);

  const buscaDtInicio = useMemo(() => {
    return searchParams.get('dhPeriodoInicio') || currentDateHour('primeira');
  }, [searchParams]);

  const buscaDtFim = useMemo(() => {
    return searchParams.get('dhPeriodoFim') || currentDateHour('ultima');
  }, [searchParams]);

  const buscaDtProxInicio = useMemo(() => {
    return searchParams.get('dhProximaExecucaoInicio') || currentDateHour('primeira');
  }, [searchParams]);

  const buscaDtProxFim = useMemo(() => {
    return searchParams.get('dhProximaExecucaoFim') || currentDateHour('ultima');
  }, [searchParams]);

  const limit = useMemo(() => {
    return searchParams.get('limit') || '10';
  }, [searchParams]);

  const offSet = useMemo(() => {
    return searchParams.get('offset') || '0';
  }, [searchParams]);

  const listaFiltros: IListaFiltros[] = [
    {
      tipo: 'TextFieldNumeric',
      mapValue: [],
      label: t('pages.workflows.idWorkflow'),
      textoBusca: buscaId,
      handleTextoBusca: texto => { searchParams.set('idWorkflow', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.workflows.nomeWorkflow'),
      textoBusca: buscaNome,
      handleTextoBusca: texto => { searchParams.set('nmWorkflow', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.workflows.descricaoWorkflow'),
      textoBusca: buscaDescricao,
      handleTextoBusca: texto => { searchParams.set('dsWorkflow', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'Select',
      mapValue: listKeyValueStatus.map((itemk) => itemk),
      label: t('pages.workflows.statusWorkflow'),
      textoBusca: buscaStatus,
      handleTextoBusca: texto => { searchParams.set('stWorkflow', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'Select',
      mapValue: listKeyValueTipo.map((itemk) => itemk),
      label: t('pages.workflows.tipoWorkflow'),
      textoBusca: buscaTipo,
      handleTextoBusca: texto => { searchParams.set('tpWorkflow', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'DateTimePicker',
      mapValue: [],
      label: t('pages.workflows.dhPeriodoInicio'),
      textoBusca: buscaDtInicio,
      handleTextoBusca: texto => { searchParams.set('dhPeriodoInicio', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'DateTimePicker',
      mapValue: [],
      label: t('pages.workflows.dhPeriodoFim'),
      textoBusca: buscaDtFim,
      handleTextoBusca: texto => { searchParams.set('dhPeriodoFim', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'DateTimePicker',
      mapValue: [],
      label: t('pages.workflows.dhProximaExecucaoInicio'),
      textoBusca: buscaDtProxInicio,
      handleTextoBusca: texto => { searchParams.set('dhProximaExecucaoInicio', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'DateTimePicker',
      mapValue: [],
      label: t('pages.workflows.dhProximaExecucaoFim'),
      textoBusca: buscaDtProxFim,
      handleTextoBusca: texto => { searchParams.set('dhProximaExecucaoFim', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
  ];
  

  const handleLimpar = () => {
    // eslint-disable-next-line prefer-const
    let parametros: string[] = [];
    searchParams.forEach(function (value, key) {
      parametros.push(key);
    });
    parametros.map((item) => (
      searchParams.delete(item)
    ));
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    searchParams.set('offset', (newPage * rowsPerPage).toString()); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    searchParams.set('limit', event.target.value.toString()); 
    searchParams.set('offset', '0');
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleCloseSnack = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setStateSnack({...stateSnack, open:false});
  };
  useEffect(() => {
    setIsLoading(true);
    WorkflowsService.getAll(Number(org?.CodigoOrganizacao), searchParams.toString())
      .then((result) => {
        setIsLoading(false);

        if (result instanceof Error) {
          setStateSnack({open:true, severity:'error',message:`Ocorreu o seguinte erro: ${result.message}`});
        } else {
          setTotalCount(result.count);
          setRows(result.rows);
          setRowsPerPage(Number.parseInt(limit.toString()));
          setPage(Number.parseInt(offSet.toString())/Number.parseInt(limit.toString()));
        }
      });

  }, [eventoPesquisar]);

 
  

  return (
    <>
      <LayoutBaseDePagina
        titulo={t('pages.workflows.workflows')}
        botaoLog={
          <Button
            color='info'
            disableElevation
            variant='contained'
            size='small'
            onClick={() => navigate('/workflow/listagem/logs')}
            startIcon={<Icon>crop</Icon>}
          >
            <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
              {t('components.ferramentasDaListagem.logs')}
            </Typography>
          </Button>
        }

        drawerDeFiltros={
          <FerramentasDaListagem
            listaFiltros={listaFiltros}
            handlePesquisar={() => setEventoPesquisar(!eventoPesquisar)}
            handleLimpar={() => handleLimpar()}
          />
        }
      >
        <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto', height: 'auto' }}>
          <Table stickyHeader >
            <TableHead>
              <TableRow>
                <TableCell width={60}>{t('global.acoes')}</TableCell>
                <TableCell>{t('pages.workflows.idWorkflow')}</TableCell>
                <TableCell>{t('pages.workflows.nomeWorkflow')}</TableCell>
                <TableCell>{t('pages.workflows.descricaoWorkflow')}</TableCell>
                <TableCell>{t('pages.workflows.statusWorkflow')}</TableCell>
                <TableCell>{t('pages.workflows.tipoWorkflow')}</TableCell>
                <TableCell>{t('pages.workflows.dhPeriodoInicio')}</TableCell>
                <TableCell>{t('pages.workflows.dhPeriodoFim')}</TableCell>
                <TableCell>{t('pages.workflows.dhLiberadoEnvio')}</TableCell>
                <TableCell>{t('pages.workflows.dhProximaExecucao')}</TableCell>
              </TableRow>
            </TableHead>
            {!isLoading && (<TableBody>
              {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow key={row.IdWorkflow}>
                  <TableCell>
                    <IconButton size="small" disabled={!hasPermitionRead} onClick={() => navigate(`/workflow/detalhe/${row.IdWorkflow}`)}>
                      <Icon>edit</Icon>
                    </IconButton>
                  </TableCell>
                  <TableCell>{row.IdWorkflow}</TableCell>
                  <TableCell>{row.NomeWorkflow}</TableCell>
                  <TableCell>{row.DescricaoWorkflow}</TableCell>
                  <TableCell>{getValueFromKeyValue(listKeyValueStatus, row.StatusWorkflow)}</TableCell>
                  <TableCell>{getValueFromKeyValue(listKeyValueTipo, row.TipoWorkflow)}</TableCell>
                  <TableCell>{stringToDateTime(row.DhPeriodoInicio)}</TableCell>
                  <TableCell>{stringToDateTime(row.DhPeriodoFim)}</TableCell>
                  <TableCell>{stringToDateTime(row.DhLiberadoEnvio)}</TableCell>
                  <TableCell>{stringToDateTime(row.DhProximaExecucao)}</TableCell>
                </TableRow>
              ))}
            </TableBody>)}

            {totalCount === 0 && !isLoading && (
              <caption>{t('global.listaVazia')}</caption>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <LinearProgress variant='indeterminate' />
                  </TableCell>
                </TableRow>
              )}
              {(!isLoading) && (
                <TableRow>
                  <TableCell colSpan={2}>
                    {hasPermitionCreate && (<Button
                      color='primary'
                      disableElevation
                      variant='outlined'
                      onClick={() => navigate('/workflow/detalhe/novo')}
                      startIcon={<Icon>add</Icon>}
                    >
                      <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                        {t('components.actions.novo')}
                      </Typography>
                    </Button>)}

                  </TableCell>
                  <TableCell colSpan={8}>
                    <TablePagination
                      component="div"
                      labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('global.of')} ${count !== -1 ? count : `${t('global.moreThan')} ${to}`}`}
                      labelRowsPerPage={t('global.rowsPerPage')}
                      showFirstButton={true}
                      showLastButton={true}
                      page={page}
                      count={rows.length}
                      rowsPerPage={rowsPerPage}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    //onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>

      </LayoutBaseDePagina>
      <AlertaSnackBar 
        open={stateSnack.open} 
        severity={stateSnack.severity as AlertColor} 
        message={stateSnack.message} 
        handleClose={handleCloseSnack} 
      />
    </>
  );
};
